// src/components/map/utils/mapExtentBbox.ts
//
// Shared bounding-box extraction for a GeoJSON extent polygon. Mapbox's
// `getBounds()` does not wrap longitude into [-180, 180] — EVA opens at
// DEFAULT_ZOOM = 5, and one zoom step out to 4 already spans ~140°, putting a
// corner around -190. Every caller that turns a viewport extent into a bbox
// needs the same clamp, so it lives here once instead of drifting across
// copies (see RigFracDataTableWidget.tsx and MapLayerSettings.tsx, both of
// which used to carry their own copy of this min/max walk).
const clampLon = (lon: number): number => Math.min(180, Math.max(-180, lon));
const clampLat = (lat: number): number => Math.min(90, Math.max(-90, lat));

/** Bounding box of a GeoJSON rectangle Polygon, clamped to valid lon/lat ranges. */
export interface MapExtentBbox {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
}

/**
 * Bounding box of a GeoJSON rectangle Polygon (as produced by `mapExtent`),
 * clamped to [-180, 180] longitude and [-90, 90] latitude. Null when the
 * polygon is empty/degenerate — the store's initial extent is an empty
 * polygon before the map has reported a viewport — or when clamping itself
 * collapses the span to zero width/height. That last case is why the
 * degeneracy check runs AFTER the clamp, not before: a viewport that sits
 * entirely past the antimeridian (e.g. -265 to -195) is non-degenerate in raw
 * lon/lat, but both corners clamp to the same -180, which would otherwise
 * send the server a zero-width bbox it answers with a 400.
 */
export function bboxFromExtentPolygon(
  polygon: GeoJSON.Polygon | undefined
): Maybe<MapExtentBbox> {
  const ring = polygon?.coordinates?.[0];
  if (!ring || ring.length === 0) return null;
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;
  for (const [lon, lat] of ring) {
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  const bbox: MapExtentBbox = {
    minLon: clampLon(minLon),
    minLat: clampLat(minLat),
    maxLon: clampLon(maxLon),
    maxLat: clampLat(maxLat)
  };
  if (!(bbox.minLon < bbox.maxLon) || !(bbox.minLat < bbox.maxLat)) return null;
  return bbox;
}
